import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

// UI Components
import {
  DrawerContainer,
  DrawerContent,
  CampaignsNavigation,
  Banner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerImage,
  gradients,
} from "../../../components/ui";

// Character image for banner
import campanasCharacter from "../../../assets/characters-banners/campañas.png";

// New Design System Components
import {
  CampaignsContainer,
  PageHeader,
  PageTitle,
  MetricsTop,
  MetricMini,
  MetricIcon,
  MetricContent,
  MetricLabel,
  MetricValue,
} from "./Campaigns.style";

// Legacy Layout Components (for step views that haven't been migrated yet)
import {
  RowFullHeight,
  Row,
} from "./BroadcastView.styles";

// Step Components
import BroadcastTable from "./BroadcastTable.component";
import BroadcastSelectTagView from "./BroadcastSelectTagView";
import BroadcastSelectFileView from "./broadcast-select-file/views/broadcast-select-file-view";
import BroadcastType from "./BroadcastType.component";
import BroadcastSelectTemplate from "./BroadcastSelectTemplate";
import BroadcastName from "./BroadcastName";
import BroadcastSelectDate from "./BroadcastSelectDate";
import BroadcastPreview from "./BroadcastPreview";
import BroadcastPayment from "./BroadcastPayment";
import BroadcastSelectMultimedia from "./BroadcastSelectMultimedia";
import BroadcastCreateTag from "./BroadcastCreateTag";
import { getWaStatusAccount } from "../../../utils/broadcast";
import LoaderComponent from "../../../components/loader/Loader.component";

const qualityRating = {
  High: "Alta",
  Medium: "Media",
  Low: "Baja",
  NA: "NA",
};

const currentLimit = {
  TIER_50: "50",
  TIER_250: "250",
  TIER_1K: "1,000",
  TIER_10K: "10,000",
  TIER_100K: "100,000",
  "TIER_0.25K": "250",
  TIER_UNLIMITED: "Ilimitado",
  NA: "NA",
};

const qualityColor = {
  High: "#166534",
  Medium: "#92400E",
  Low: "#991B1B",
};

const BroadcastView = (props) => {
  const history = useHistory();
  const waChannels = useSelector(
    (state) => state.channels.data.whatsappChannels
  );
  const channelId = useSelector((state) => state.campaign.create.channelId);

  const [selectedChannel, setSelectedChannel] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState("create");

  const [accountStatus, setAccountStatus] = useState({
    current_limit: "NA",
    current_quality_rating: "NA",
  });

  const currentStep = props?.computedMatch?.params?.currentStep;

  useEffect(() => {
    if (waChannels?.length) {
      setSelectedChannel(waChannels[0]);
      fetchWaStatusAccount(waChannels[0]);
    }
  }, [waChannels]);

  useEffect(() => {
    if (!channelId) history.push("/broadcast/table");
  }, [channelId]);

  const fetchWaStatusAccount = async (ch) => {
    try {
      const response = await getWaStatusAccount(ch?.providerId);
      setAccountStatus(response);
    } catch (e) {
      console.log("ERROR AT FETCHING WA STATUS => ", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChannelChange = useCallback((channelId) => {
    const channel = waChannels.find((ch) => ch.id === channelId);
    setSelectedChannel(channel);
    fetchWaStatusAccount(channel);
  }, [waChannels]);

  const handleViewChange = (viewKey) => {
    setActiveView(viewKey);
    // Navigate based on view selection
    if (viewKey === "create") {
      history.push("/broadcast/table");
    }
    // Other views (scheduled, audiences, reports) are disabled for now
  };

  // Render Metrics Section
  const renderMetrics = () => (
    <MetricsTop>
      <MetricMini>
        <MetricIcon bg="#EEF2FF" color="#4F46E5">
          <FontAwesomeIcon icon={faStar} />
        </MetricIcon>
        <MetricContent>
          <MetricLabel>Calidad de tu cuenta</MetricLabel>
          <MetricValue color={qualityColor[accountStatus["current_quality_rating"]] || "#166534"}>
            {isLoading ? (
              <LoaderComponent width="22" height="22" />
            ) : (
              qualityRating[accountStatus["current_quality_rating"]] || "Alta"
            )}
          </MetricValue>
        </MetricContent>
      </MetricMini>
      <MetricMini>
        <MetricIcon bg="#F0FDF4" color="#16A34A">
          <FontAwesomeIcon icon={faInfoCircle} />
        </MetricIcon>
        <MetricContent>
          <MetricLabel>Límite de envío diario</MetricLabel>
          <MetricValue>
            {isLoading ? (
              <LoaderComponent width="22" height="22" />
            ) : (
              <>
                {currentLimit[accountStatus["current_limit"]] || "250"}{" "}
                <span>mensajes</span>
              </>
            )}
          </MetricValue>
        </MetricContent>
      </MetricMini>
    </MetricsTop>
  );

  // Render the main content based on current step
  const renderContent = () => {
    // For table view - render with new design
    if (currentStep === "table") {
      return (
        <CampaignsContainer>
          {/* Banner Section */}
          <Banner gradient={gradients.orange}>
            <BannerContent>
              <BannerTitle>Campañas de WhatsApp</BannerTitle>
              <BannerDescription>
                Envía mensajes masivos a tus contactos con plantillas aprobadas. Programa campañas y analiza resultados.
              </BannerDescription>
            </BannerContent>
            <BannerImage src={campanasCharacter} alt="Campañas" />
          </Banner>

          <PageHeader>
            <PageTitle>Mis Campañas</PageTitle>
            {renderMetrics()}
          </PageHeader>
          <RowFullHeight>
            <BroadcastTable
              qualityRating={qualityRating[selectedChannel?.qualityRating]}
              onChannelChange={handleChannelChange}
            />
          </RowFullHeight>
        </CampaignsContainer>
      );
    }

    // For type selection - render with new design (no metrics)
    if (currentStep === "type") {
      return (
        <CampaignsContainer>
          <RowFullHeight>
            <BroadcastType />
          </RowFullHeight>
        </CampaignsContainer>
      );
    }

    // For other steps - use legacy layout for now
    return (
      <CampaignsContainer style={{ height: '100%' }}>
        {currentStep === "file" && (
          <RowFullHeight>
            <BroadcastSelectFileView />
          </RowFullHeight>
        )}

        {currentStep === "tags" && (
          <RowFullHeight>
            <BroadcastSelectTagView
              accountLimitOfMessages={accountStatus["current_limit"] || 250}
            />
          </RowFullHeight>
        )}

        {currentStep === "selectTemplate" && (
          <RowFullHeight>
            <BroadcastSelectTemplate />
          </RowFullHeight>
        )}

        {currentStep === "selectDate" && (
          <RowFullHeight>
            <BroadcastSelectDate />
          </RowFullHeight>
        )}

        {currentStep === "preview" && (
          <RowFullHeight>
            <BroadcastPreview />
          </RowFullHeight>
        )}

        {currentStep === "pricing" && (
          <RowFullHeight>
            <BroadcastPayment accountLimitOfMessages={accountStatus["current_limit"] || 250} />
          </RowFullHeight>
        )}

        {currentStep === "createTag" && (
          <RowFullHeight>
            <BroadcastCreateTag />
          </RowFullHeight>
        )}

        {currentStep === "name" && (
          <RowFullHeight>
            <BroadcastName
              accountLimitOfMessages={accountStatus["current_limit"] || 250}
            />
          </RowFullHeight>
        )}

        {currentStep === "selectMultimedia" && (
          <Row>
            <BroadcastSelectMultimedia />
          </Row>
        )}
      </CampaignsContainer>
    );
  };

  return (
    <DrawerContainer>
      <CampaignsNavigation activeView={activeView} onViewChange={handleViewChange} />
      <DrawerContent>
        {renderContent()}
      </DrawerContent>
    </DrawerContainer>
  );
};

export default BroadcastView;
